// --- Constants ---
export const MAX_POPULATION = 300;
export const DEFAULT_DOT_COLOR = '#9ca3af';
export const DEFAULT_DOT_RADIUS = 1.2;
export const SELECTED_DOT_RADIUS = 1.8;

const SVG_VIEWBOX_SIZE = 100;
const SVG_PADDING = 5;
const USABLE_SIZE = SVG_VIEWBOX_SIZE - 2 * SVG_PADDING;

/** SVG coordinate extent for the biased-sampling zone (top-left corner). */
export const BIAS_ZONE_EXTENT = 33;

// --- Config interfaces ---

export type SampleMethod = 'random' | 'systematic' | 'stratified' | 'biased' | 'quota' | 'convenience';

/**
 * Visual mode for missing data dots:
 * - `highlight`: missing dots shown as hollow circles with dashed stroke (intro)
 * - `removed`: missing dots disappear completely (listwise deletion)
 * - `dimmed`: missing dots stay but very faint (pairwise deletion — still used where available)
 * - `imputed`: missing dots shown in a distinct color (imputation — estimated values)
 */
export type MissingDataMode = 'highlight' | 'removed' | 'dimmed' | 'imputed';

export interface DemographicCategory {
  label: string;
  percentage: number; // 0-100
  color: string; // hex color
}

export interface ReliabilityBar {
  label: string;
  width: number; // percentage 0-100
  color: string;
}

export interface PopulationWidgetParams {
  populationSize: number; // 0 to MAX_POPULATION

  // Base dot appearance
  dotOpacity?: number; // default 1

  // Demographics (single dimension)
  demographicName?: string;
  demographicCategories?: DemographicCategory[];

  // Sample selection
  sampleMethod?: SampleMethod;
  sampleColor?: string;
  sampleSeed?: number; // for random / stratified (default 42)
  samplePercentage?: number; // percentage of population to sample (preferred over sampleSize)
  sampleSize?: number; // absolute count (fallback when samplePercentage is not set)
  sampleInterval?: number; // for systematic: every Nth item (default 10)

  // Display
  showPopulationCount?: boolean; // default true — set false to hide the "Population: N" label

  // Overlays
  showStratifiedDivider?: boolean;
  showBiasZone?: boolean;

  // Reliability bars
  reliabilityBars?: ReliabilityBar[];

  // Missing data visualisation
  missingPercentage?: number; // percentage of dots to mark as missing (0-100)
  missingDataMode?: MissingDataMode; // how to render missing dots
  missingSeed?: number; // seed for deterministic missing selection (default 123)
  imputedColor?: string; // color for imputed dots (default '#3b82f6')
}

// --- Internal dot state ---

export interface PopulationDot {
  id: number;
  cx: number;
  cy: number;
  r: number;
  color: string;
  opacity: number;
  /** True when this dot represents a missing data point (rendered differently in the template). */
  isMissing?: boolean;
}

// --- Grid computation ---

export function computeGridDimensions(size: number): { cols: number; rows: number } {
  if (size <= 0) {
    return { cols: 1, rows: 1 };
  }
  const cols = Math.ceil(Math.sqrt(size));
  const rows = Math.ceil(size / cols);
  return { cols, rows };
}

// --- Demographic helpers ---

export function normalizePercentages(categories: DemographicCategory[]): DemographicCategory[] {
  if (categories.length === 0) {
    return [];
  }
  const total = categories.reduce((sum, c) => sum + c.percentage, 0);
  if (total === 0) {
    const even = 100 / categories.length;
    return categories.map((c) => ({ ...c, percentage: even }));
  }
  const scale = 100 / total;
  return categories.map((c) => ({ ...c, percentage: c.percentage * scale }));
}

export function assignCategory(
  dotIndex: number,
  totalActive: number,
  categories: DemographicCategory[],
): DemographicCategory | undefined {
  if (categories.length === 0 || totalActive === 0) {
    return undefined;
  }
  const fraction = dotIndex / totalActive;
  let cumulative = 0;
  for (const cat of categories) {
    cumulative += cat.percentage / 100;
    if (fraction < cumulative) {
      return cat;
    }
  }
  return categories[categories.length - 1];
}

// --- Selection algorithms ---

export function seededRandomSelection(total: number, count: number, seed: number): number[] {
  const indices = Array.from({ length: total }, (_, i) => i);
  let s = seed;
  for (let i = indices.length - 1; i > 0; i--) {
    s = (s * 16807) % 2147483647;
    const j = s % (i + 1);
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  return indices.slice(0, count);
}

export function systematicSelection(total: number, interval: number, start = 0): number[] {
  const result: number[] = [];
  for (let i = start; i < total; i += interval) {
    result.push(i);
  }
  return result;
}

export function stratifiedSelection(
  total: number,
  count: number,
  categories: DemographicCategory[],
  seed = 42,
): number[] {
  if (categories.length === 0) {
    return seededRandomSelection(total, count, seed);
  }
  const normalized = normalizePercentages(categories);
  const result: number[] = [];
  let dotStart = 0;

  for (const cat of normalized) {
    const groupSize = Math.round((total * cat.percentage) / 100);
    const sampleFromGroup = Math.round((count * cat.percentage) / 100);
    // Randomly sample within each stratum (not just the first N)
    const selected = seededRandomSelection(groupSize, sampleFromGroup, seed + dotStart);
    for (const idx of selected) {
      result.push(dotStart + idx);
    }
    dotStart += groupSize;
  }
  return result.slice(0, count);
}

/**
 * Quota selection: non-random, consecutive picks from each demographic group.
 * Visually produces clustered blocks (not scattered), showing the researcher
 * fills each group's target without random selection.
 */
export function quotaSelection(totalSize: number, count: number, categories: DemographicCategory[]): number[] {
  const normalized = normalizePercentages(categories);
  const result: number[] = [];
  let dotStart = 0;
  let remaining = count;

  for (let i = 0; i < normalized.length; i++) {
    const groupSize = Math.round((totalSize * normalized[i].percentage) / 100);
    const quota = i === normalized.length - 1 ? remaining : Math.round((count * normalized[i].percentage) / 100);

    const toTake = Math.min(quota, groupSize);
    for (let j = 0; j < toTake; j++) {
      result.push(dotStart + j);
    }

    dotStart += groupSize;
    remaining -= quota;
  }

  return result;
}

interface BaseDot {
  id: number;
  cx: number;
  cy: number;
}

export function biasedSelection(dots: BaseDot[], count: number): number[] {
  return dots
    .filter((d) => d.cx < BIAS_ZONE_EXTENT && d.cy < BIAS_ZONE_EXTENT)
    .map((d) => d.id)
    .slice(0, count);
}

// --- Main computation ---

function computeBaseDots(size: number): BaseDot[] {
  const { cols, rows } = computeGridDimensions(size);
  // Use the larger dimension for both axes so dot spacing is equal → square grid
  const side = Math.max(cols, rows);
  const xOffset = (((side - cols) / side) * USABLE_SIZE) / 2;
  const yOffset = (((side - rows) / side) * USABLE_SIZE) / 2;
  const dots: BaseDot[] = [];
  for (let i = 0; i < size; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    dots.push({
      id: i,
      cx: SVG_PADDING + xOffset + ((col + 0.5) / side) * USABLE_SIZE,
      cy: SVG_PADDING + yOffset + ((row + 0.5) / side) * USABLE_SIZE,
    });
  }
  return dots;
}

// --- Stratified grid layout ---

export interface StratifiedGridSection {
  xStart: number;
  width: number;
  cols: number;
  dotCount: number;
  label: string;
  percentage: number;
}

export interface StratifiedGridInfo {
  sections: StratifiedGridSection[];
  dividerXPositions: number[];
  rows: number;
  cellSize: number;
  yBase: number;
  yEnd: number;
}

const STRATIFIED_DIVIDER_GAP = 4;

export function computeStratifiedGrid(size: number, categories: DemographicCategory[]): StratifiedGridInfo {
  const normalized = normalizePercentages(categories);

  const dotCounts: number[] = [];
  let remaining = size;
  for (let i = 0; i < normalized.length; i++) {
    if (i === normalized.length - 1) {
      dotCounts.push(remaining);
    } else {
      const count = Math.round((size * normalized[i].percentage) / 100);
      dotCounts.push(count);
      remaining -= count;
    }
  }

  const { rows } = computeGridDimensions(size);
  const colsPerSection = dotCounts.map((count) => (count > 0 ? Math.ceil(count / rows) : 0));
  const totalCols = colsPerSection.reduce((a, b) => a + b, 0);

  const numGaps = normalized.length - 1;
  const totalGapWidth = numGaps * STRATIFIED_DIVIDER_GAP;
  const availWidth = USABLE_SIZE - totalGapWidth;
  const cellSize = Math.min(availWidth / totalCols, USABLE_SIZE / rows);

  const gridWidth = totalCols * cellSize + totalGapWidth;
  const gridHeight = rows * cellSize;
  const xBase = SVG_PADDING + (USABLE_SIZE - gridWidth) / 2;
  const yBase = SVG_PADDING + (USABLE_SIZE - gridHeight) / 2;

  const sections: StratifiedGridSection[] = [];
  const dividerXPositions: number[] = [];
  let colOffset = 0;

  for (let s = 0; s < normalized.length; s++) {
    const cols = colsPerSection[s];
    const xStart = xBase + colOffset * cellSize + s * STRATIFIED_DIVIDER_GAP;
    const width = cols * cellSize;

    sections.push({
      xStart,
      width,
      cols,
      dotCount: dotCounts[s],
      label: normalized[s].label,
      percentage: normalized[s].percentage,
    });

    colOffset += cols;

    if (s < normalized.length - 1) {
      dividerXPositions.push(xStart + width + STRATIFIED_DIVIDER_GAP / 2);
    }
  }

  return { sections, dividerXPositions, rows, cellSize, yBase, yEnd: yBase + gridHeight };
}

function computeStratifiedBaseDots(size: number, categories: DemographicCategory[]): BaseDot[] {
  const grid = computeStratifiedGrid(size, categories);
  const dots: BaseDot[] = [];
  let dotId = 0;

  for (const section of grid.sections) {
    for (let i = 0; i < section.dotCount; i++) {
      const col = i % section.cols;
      const row = Math.floor(i / section.cols);
      dots.push({
        id: dotId++,
        cx: section.xStart + (col + 0.5) * grid.cellSize,
        cy: grid.yBase + (row + 0.5) * grid.cellSize,
      });
    }
  }

  return dots;
}

function getEffectiveSampleSize(params: PopulationWidgetParams): number | undefined {
  if (params.samplePercentage !== null && params.samplePercentage !== undefined) {
    return Math.round((params.populationSize * params.samplePercentage) / 100);
  }
  return params.sampleSize;
}

function computeSelectedSet(baseDots: BaseDot[], params: PopulationWidgetParams): Set<number> | undefined {
  const { sampleMethod } = params;
  if (!sampleMethod) {
    return undefined;
  }

  const size = baseDots.length;
  const seed = params.sampleSeed ?? 42;
  let indices: number[];

  switch (sampleMethod) {
    case 'random': {
      const count = getEffectiveSampleSize(params);
      if (!count) {
        return undefined;
      }
      indices = seededRandomSelection(size, count, seed);
      break;
    }
    case 'systematic': {
      const interval = params.sampleInterval ?? 10;
      indices = systematicSelection(size, interval);
      break;
    }
    case 'stratified': {
      const count = getEffectiveSampleSize(params);
      if (!count) {
        return undefined;
      }
      const categories = params.demographicCategories;
      indices = categories?.length
        ? stratifiedSelection(size, count, categories, seed)
        : seededRandomSelection(size, count, seed);
      break;
    }
    case 'biased':
    case 'convenience': {
      const count = getEffectiveSampleSize(params);
      if (!count) {
        return undefined;
      }
      indices = biasedSelection(baseDots, count);
      break;
    }
    case 'quota': {
      const count = getEffectiveSampleSize(params);
      if (!count) {
        return undefined;
      }
      const categories = params.demographicCategories;
      indices = categories?.length ? quotaSelection(size, count, categories) : biasedSelection(baseDots, count);
      break;
    }
    default:
      return undefined;
  }

  return new Set(indices);
}

function computeMissingSet(size: number, params: PopulationWidgetParams): Set<number> | undefined {
  const pct = params.missingPercentage;
  if (!pct || pct <= 0) {
    return undefined;
  }
  const count = Math.round((size * Math.min(pct, 100)) / 100);
  const seed = params.missingSeed ?? 123;
  return new Set(seededRandomSelection(size, count, seed));
}

export const MISSING_HIGHLIGHT_COLOR = '#ef4444';
export const DEFAULT_IMPUTED_COLOR = '#3b82f6';

export function computeDots(params: PopulationWidgetParams): PopulationDot[] {
  const size = Math.max(0, Math.min(MAX_POPULATION, Math.round(params.populationSize)));
  const baseDots =
    params.showStratifiedDivider && params.demographicCategories?.length
      ? computeStratifiedBaseDots(size, params.demographicCategories)
      : computeBaseDots(size);
  const normalizedCategories = params.demographicCategories?.length
    ? normalizePercentages(params.demographicCategories)
    : undefined;

  const selectedSet = computeSelectedSet(baseDots, params);
  const missingSet = computeMissingSet(size, params);
  const missingMode = params.missingDataMode ?? 'highlight';
  const baseOpacity = params.dotOpacity ?? 1;
  const hasSample = !!selectedSet;
  const dots: PopulationDot[] = [];

  for (let i = 0; i < MAX_POPULATION; i++) {
    if (i < size) {
      const base = baseDots[i];
      const isSelected = selectedSet?.has(i) ?? false;
      const isMissing = missingSet?.has(i) ?? false;
      const category = normalizedCategories ? assignCategory(i, size, normalizedCategories) : undefined;

      let color: string;
      let r: number;
      let opacity: number;

      // Missing data takes priority over sampling visuals
      if (isMissing) {
        switch (missingMode) {
          case 'removed':
            // Dot disappears (listwise deletion)
            r = 0;
            opacity = 0;
            color = DEFAULT_DOT_COLOR;
            break;
          case 'dimmed':
            // Dot stays but very faint (pairwise deletion)
            r = DEFAULT_DOT_RADIUS;
            opacity = 0.15;
            color = category?.color ?? DEFAULT_DOT_COLOR;
            break;
          case 'imputed':
            // Dot shown in imputed color (imputation)
            r = SELECTED_DOT_RADIUS;
            opacity = 1;
            color = params.imputedColor ?? DEFAULT_IMPUTED_COLOR;
            break;
          case 'highlight':
          default:
            // Dot shown as hollow/highlighted (intro — "these are missing")
            r = SELECTED_DOT_RADIUS;
            opacity = 1;
            color = MISSING_HIGHLIGHT_COLOR;
            break;
        }
        dots.push({ id: i, cx: base.cx, cy: base.cy, r, color, opacity, isMissing: missingMode === 'highlight' });
      } else if (isSelected) {
        color = category?.color ?? params.sampleColor ?? '#22c55e';
        r = SELECTED_DOT_RADIUS;
        opacity = 1;
        dots.push({ id: i, cx: base.cx, cy: base.cy, r, color, opacity });
      } else if (hasSample) {
        color = DEFAULT_DOT_COLOR;
        r = DEFAULT_DOT_RADIUS;
        opacity = 0.35;
        dots.push({ id: i, cx: base.cx, cy: base.cy, r, color, opacity });
      } else {
        color = category?.color ?? DEFAULT_DOT_COLOR;
        r = DEFAULT_DOT_RADIUS;
        opacity = baseOpacity;
        dots.push({ id: i, cx: base.cx, cy: base.cy, r, color, opacity });
      }
    } else {
      dots.push({
        id: i,
        cx: SVG_VIEWBOX_SIZE / 2,
        cy: SVG_VIEWBOX_SIZE / 2,
        r: 0,
        color: DEFAULT_DOT_COLOR,
        opacity: 0,
      });
    }
  }

  return dots;
}
